import { getRefreshToken, getSessionToken, useDescope, useSession, useUser } from "@descope/react-sdk";
import Logout from "@mui/icons-material/Logout";
import { CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import apiRoutes from "../../constants/api-routes";
import {
	selectUser,
	setEmail,
	setFirstName,
	setGroups,
	setIsCoAppSuperAdmin,
	setLastName,
	setOrganizationKillSwitch,
	setOrganizationName,
	setOrgId,
	setPermissions,
	setPhone,
	setRoles,
	setUserId
} from "../../redux/userSlice";
import CoAppDescopeLoginFlow from "../login/CoAppDescopeLoginFlow";
import {
	CoAppLoginCard,
	CoAppLoginContainer,
	CoAppLoginFooter,
	CoAppLoginLogo
} from "../global/styled/global.styled";
import { SessionValidationCardStructure } from "../global/styled/session-validation-card.styled";

export default function ExtensionSignIn() {
	const dispatch = useDispatch();
	const { logout } = useDescope();
	const currentUser = useSelector(selectUser);

	const { isAuthenticated, isSessionLoading } = useSession();
	const { isUserLoading } = useUser();

	const handleUserLogout = useCallback(async () => {
		await logout();
		dispatch(setEmail(""));
		dispatch(setUserId(null));
		dispatch(setFirstName(""));
		dispatch(setGroups([]));
		dispatch(setLastName(""));
		dispatch(setPermissions([]));
		dispatch(setPhone(""));
		dispatch(setRoles([]));
		dispatch(setOrgId(null));
		dispatch(setIsCoAppSuperAdmin(false));
		dispatch(setOrganizationName(""));
	}, [logout, dispatch]);

	const throwSuccessEvent = () => {
		const sessionToken = getSessionToken();
		const refreshToken = getRefreshToken();
		const decodedJwt = jwtDecode(sessionToken) as { tenantId?: string };
		axios.get(apiRoutes.getUser(1, "", currentUser.email?.toLowerCase() ?? "", decodedJwt?.tenantId ?? ""), { headers: { "Authorization": "Bearer " + sessionToken } })
			.then(res => {
				dispatch(setFirstName(res.data.firstname));
				dispatch(setLastName(res.data.lastname));
				dispatch(setGroups(res.data.groups));
				dispatch(setUserId(res.data.id));
				dispatch(setIsCoAppSuperAdmin(res.data.isCoAppSupport));
				dispatch(setPhone(res.data.phone));
				dispatch(setOrganizationKillSwitch(res.data.organizationKillSwitch));
				dispatch(setOrganizationName(res.data.organization?.name));
				dispatch(setOrgId(res.data.organizationId));
				dispatch(setPermissions(res.data.permissions));
				dispatch(setRoles(res.data.roles));
				const event = new CustomEvent("extensionAuthenticationComplete", {
					detail: {
						user: {
							accessToken: sessionToken,
							refreshToken: refreshToken,
							email: res.data.email,
							firstName: res.data.firstname,
							lastName: res.data.lastname,
							permissions: res.data.permissions,
							isCoAppSupport: res.data.isCoAppSupport,
							organizationName: res.data.organization?.name,
							organizationId: res.data.organizationId,
							clientId: res.data.organization?.clientId,
						},
						accessToken: sessionToken,
						refreshToken: refreshToken
					}
				});
				document.dispatchEvent(event);
			})
			.catch(err => {
				console.error(err);
			});
	};

	const handleLoginAndThrowSuccessEvent = (e) => {
		const decodedJwt = jwtDecode(e.detail.sessionJwt) as { email?: string; tenantId?: string };
		const email = decodedJwt.email?.toLowerCase();
		let tenantId;
		if (decodedJwt.tenantId) {
			tenantId = decodedJwt.tenantId;
		}
		dispatch(setEmail(email ?? ""));
		axios.get(apiRoutes.getUser(1, "", email ?? "", tenantId ?? ""), { headers: { "Authorization": "Bearer " + e.detail.sessionJwt } })
			.then(res => {
				dispatch(setFirstName(res.data.firstname));
				dispatch(setLastName(res.data.lastname));
				dispatch(setGroups(res.data.groups));
				dispatch(setUserId(res.data.id));
				dispatch(setPhone(res.data.phone));
				dispatch(setOrganizationKillSwitch(res.data.organizationKillSwitch));
				dispatch(setOrgId(res.data.organizationId));
				dispatch(setPermissions(res.data.permissions));
				dispatch(setIsCoAppSuperAdmin(res.data.isCoAppSupport));
				dispatch(setOrganizationName(res.data.organization?.name));

				const event = new CustomEvent("extensionAuthenticationComplete",
					{
						detail: {
							user: {
								email: email,
								firstName: res.data.firstname,
								lastName: res.data.lastname,
								permissions: res.data.permissions,
								accessToken: e.detail.sessionJwt,
								refreshToken: e.detail.refreshJwt,
								isCoAppSupport: res.data.isCoAppSupport,
								organizationName: res.data.organization?.name,
								organizationId: res.data.organizationId,
								clientId: res.data.organization?.clientId,
							},
							accessToken: e.detail.sessionJwt,
							refreshToken: e.detail.refreshJwt
						}
					}
				);
				document.dispatchEvent(event);
			})
			.catch(err => {
				console.error(err);
			});
	};

	useEffect(() => {
		if (!isSessionLoading) {
			if (isAuthenticated) {
				throwSuccessEvent();
			} else {
				//handleUserLogout();
			}
		}
	}, [isAuthenticated]);

	const coappLoginFooter = (
		<CoAppLoginFooter>
			<a href="https://coapp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
			<span aria-hidden="true">·</span>
			<a href="https://coapp.io/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
			<span aria-hidden="true">·</span>
			<a href="mailto:support@coapp.io">Support</a>
		</CoAppLoginFooter>
	);

	if (isAuthenticated) {
		return (
			<SessionValidationCardStructure>
				<Stack spacing={2} sx={{ alignItems: "center", textAlign: "center", width: "100%" }}>
					<CoAppLoginLogo src="/images/CoApp-Logo-COLOR-RGB.png" alt="CoApp" />
					<Typography variant="body1" sx={{ color: "rgba(13, 31, 48, 0.85)", fontWeight: 500 }}>
						Thanks for logging in. You can close this tab now.
					</Typography>
					<Tooltip title="Logout">
						<IconButton onClick={handleUserLogout} aria-label="Logout" sx={{ color: "rgba(13, 31, 48, 0.55)" }}>
							<Logout />
						</IconButton>
					</Tooltip>
				</Stack>
				{coappLoginFooter}
			</SessionValidationCardStructure>
		);
	}

	if (isSessionLoading || isUserLoading) {
		return (
			<CoAppLoginContainer>
				<CoAppLoginCard>
					<Stack spacing={2} sx={{ py: 4, alignItems: "center" }}>
						<CircularProgress sx={{ color: "#02D5AB" }} size={40} thickness={4} />
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)" }}>
							Signing you in…
						</Typography>
					</Stack>
					{coappLoginFooter}
				</CoAppLoginCard>
			</CoAppLoginContainer>
		);
	}

	return (
		<CoAppLoginContainer>
			<CoAppLoginCard>
				<CoAppDescopeLoginFlow onSuccess={handleLoginAndThrowSuccessEvent} />
				{coappLoginFooter}
			</CoAppLoginCard>
		</CoAppLoginContainer>
	);
}
